import { useLocation, useNavigate, useParams } from "react-router-dom";
import { disable, idcard, info, trashOutline } from "../../../asset";
import PageHeader from "../../../components/PageHeader";
import Tabs from "../../../components/Tabs";
import PersonalInformation from "./PersonalInformation";
import StaffExitDetails from "./StaffExitDetails";
import { Staff } from "../../../types/staff";
import { JSX, useEffect, useState } from "react";
import ClassAccess from "../class-access/ClassAccess";
import Permissions from "../permissions/Permissions";
import AdminFeatures from "../permissions/AdminFeatures";
import PersonalDetails from "./PersonalDetails";
import FinancialDetails from "./FinancialDetails";
import DocumentDetails from "./DocumentDetails";
import {
  EditModeProvider,
  useEditMode,
} from "../../../context/EditModeContext";
import useUrlTab from "../../../util/hooks/useUrlTab";
import AssignSubject from "../assign-subject/AssignSubject";
import useConfirmToast from "../../../util/hooks/useConfirmToast";
import SubButton from "../../../components/SubButtons";
import { toast } from "sonner";
import StaffApis from "../../../api/staffApis";
import UpdateDetailsPayroll from "../../../components/modal/UpdateDetailsPayroll";
import { CustomButton } from "../../../components";
import { IS_LIVE } from "../../../util/config/env";

const tabOptions = [
  "Employee Details",
  "Class Access",
  "Assigned Subjects",
  "Permissions",
];

function StaffProfileContent({ staffId }: { staffId?: number }) {
  const { isEditing, toggleEdit } = useEditMode();
  const { activeTab, handleTabChange } = useUrlTab(tabOptions);
  const { confirm, modal } = useConfirmToast();
  const navigate = useNavigate();

  const [staffData, setStaffData] = useState<Staff | undefined>();
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(
    location.state?.showReminder || false,
  );

  const handleChildChange = (updatedFields: Partial<Staff>) => {
    setStaffData((prev) => (prev ? { ...prev, ...updatedFields } : prev));
  };

  const fetchStaff = async () => {
    if (!staffId) return;

    setLoading(true);
    const response = await StaffApis.getStaffById(Number(staffId));

    if (response?.data) {
      setStaffData(response.data);
    }

    setLoading(false);
  };

  const isEnabled = staffData?.active ?? false;

  useEffect(() => {
    fetchStaff();
  }, [staffId]);

  useEffect(() => {
    if (location.state?.scrollTo === "financial" && !loading) {
      setTimeout(() => {
        document
          .getElementById("financial-details")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location.state, loading]);

  const handleToggleEnable = async () => {
    if (!staffData) return;

    const confirmed = await confirm(
      isEnabled
        ? "Are you sure you want to disable this profile?"
        : "Are you sure you want to enable this profile?",
      { confirmText: "Yes" },
    );

    if (!confirmed) return;

    try {
      await StaffApis.toggleActivate(Number(staffId));

      // Update the source of truth directly
      setStaffData((prev) => (prev ? { ...prev, active: !prev.active } : prev));

      toast.success(
        `Staff ${!isEnabled ? "enabled" : "disabled"} successfully`,
      );
    } catch (error) {
      toast.error("Failed to update staff status");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!staffData?.staffId) return;

    const { staffId, academicCycles, features, ...cleanData } = staffData;

    const payload = {
      ...cleanData,
      academicCycles: [], // Send empty as per your working curl
      features: [], // Send empty as per your working curl
    };

    try {
      const result = await StaffApis.updateStaff(staffData.staffId, payload);

      if (result) {
        toast.success("Staff profile updated successfully");
        toggleEdit(); // Turn off edit mode
      }
    } catch (error) {
      toast.error("Failed to update staff details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6">Loading staff...</div>;

  const tabContent: Record<string, JSX.Element> = {
    "Employee Details": (
      <>
        <PersonalInformation staff={staffData} onChange={handleChildChange} />
        <PersonalDetails staff={staffData} onChange={handleChildChange} />
        <FinancialDetails staff={staffData} onChange={handleChildChange} />
        <DocumentDetails staff={staffData} onChange={handleChildChange} />
        <div>
          <StaffExitDetails staff={staffData} onChange={handleChildChange} />
          <div className="flex justify-end mb-3 px-4 items-end relative group">
            <CustomButton
              color="bg-primary-500"
              text="Submit"
              disabled={!isEditing}
              onClick={handleUpdate}
            />
          </div>
        </div>
      </>
    ),
    "Class Access": (
      <div className="w-full overflow-x-auto">
        <div className="min-w-[800px] max-w-6xl">
          <ClassAccess
            staffId={staffId}
            academicCycles={staffData?.academicCycles || []}
            onRefresh={fetchStaff}
          />
        </div>
      </div>
    ),
    "Assigned Subjects": (
      <div className="w-full overflow-x-auto">
        <div className="min-w-[400px] max-w-3xl">
          <AssignSubject staffId={staffId} />{" "}
        </div>
      </div>
    ),
    Permissions: (
      <div className="w-full overflow-x-auto">
        <div className="min-w-[800px] max-w-6xl">
          <Permissions
            staffId={staffId}
            features={staffData?.features || []}
            onRefresh={fetchStaff}
          />
        </div>
      </div>
    ),
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm(
      "Are you sure you want to delete this profile",
    );

    if (!confirmed) return;

    try {
      await StaffApis.deleteStaff(id);

      toast.success("Staff deleted successfully");

      // Go back to staff list
      navigate("/staff");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete staff");
    }
  };

  return (
    <div className="flex flex-col gap-5 overflow-hidden">
      <div className="flex flex-col md:flex-row gap-4 justify-between md:items-center">
        <PageHeader
          showBack
          onBack={() => navigate("/staff")}
          title={`staff profile - ${staffData?.fullName}`}
        />
        <div className="flex justify-end items-end gap-2">
          {!IS_LIVE && (
            <>
              <SubButton
                text={isEnabled ? "Disable" : "Enable"}
                color={isEnabled ? "bg-background-800" : "bg-primary-500"}
                icon={isEnabled ? disable : ""}
                onClick={handleToggleEnable}
              />
              <SubButton
                text="ID Card"
                color="bg-success-600"
                icon={idcard}
                onClick={() =>
                  navigate(
                    `/staff/staff-profile/staff-id-card/${staffData.staffId}`,
                    {
                      state: { staffData },
                    },
                  )
                }
              />
            </>
          )}
          <SubButton
            text="Delete"
            color="bg-error-600"
            icon={trashOutline}
            onClick={() => handleDelete(staffData?.staffId!)}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg w-full grid py-5 divide-y divide-neutral-50">
        <div className="w-full flex flex-col lg:flex-row justify-between gap-4 lg:items-center overflow-x-auto lg:overflow-visible">
          <div className="lg:flex-1 flex justify-center items-center">
            <Tabs
              tabs={tabOptions}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </div>
          <div className="flex justify-end items-end px-14">
            {/* {activeTab === "Employee Details" && ( */}

            <button
              onClick={() => {
                if (!isEnabled) {
                  toast.warning("Enable this staff profile to edit details");
                  return;
                }
                toggleEdit();
              }}
              className={`
    py-1 px-5 rounded font-semibold text-[13px] text-white
    ${
      !isEnabled
        ? "bg-neutral-300 cursor-not-allowed"
        : isEditing
          ? "bg-error-500"
          : "bg-warning-600"
    }
  `}
            >
              {isEditing ? "Cancel" : "Edit"}
            </button>

            {/* )} */}
          </div>
        </div>
        {tabContent[activeTab]}
      </div>
      {modal}

      {showPopup && (
        <UpdateDetailsPayroll
          icon={info}
          description="Please complete the entire profile of your Staff to add and manage their payroll."
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
}

export default function ViewStaffProfile() {
  const { staffId } = useParams<{ staffId: string }>();

  return (
    <EditModeProvider>
      <StaffProfileContent staffId={Number(staffId)} />
    </EditModeProvider>
  );
}
